import { safeNext } from '@miroom/shared';
import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { FullscreenSpinner } from '../components/FullscreenSpinner';
import { useAuth } from './useAuth';

/** Loads the current session once per app start. */
export function SessionBootstrap({ children }: { children: ReactNode }) {
  const { status, refresh } = useAuth();
  useEffect(() => {
    if (status === 'unknown') void refresh();
  }, [status, refresh]);
  return <>{children}</>;
}

/** Only signed-in users; others go to /login?next=<current>. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'unknown') return <FullscreenSpinner />;
  if (status === 'guest') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** Login/register pages: signed-in users are sent on. */
export function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [params] = useSearchParams();
  if (status === 'unknown') return <FullscreenSpinner />;
  if (status === 'authenticated') return <Navigate to={safeNext(params.get('next'))} replace />;
  return <>{children}</>;
}
